import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { InternalCompany, useInternalCompaniesQuery } from '../../graphql/types';
import { debounce, delay } from 'lodash';
import { Autocomplete, ClickAwayListener, Stack, TextField } from '@mui/material';
import AssetLogo from '../AssetLogo';
import AssetListButtonOption from '../AssetListButtonOption';
import { useFormikContext } from 'formik';
interface FormValues {
  company: {
    id?: string;
    logo?: string | null;
    legalName?: string | null;
    isDefault?: boolean;
  } | null;
}

export interface Props {
  id: string;
  disabled: boolean;
  defaultCompany?: boolean;
  fundingCompany?: boolean;
}

export const InternalCompanyAutocomplete = ({ id, disabled, defaultCompany = false, fundingCompany = false }: Props) => {
  const companyTypeLabel = fundingCompany ? 'Funding Company' : 'Company';
  const placeholder = fundingCompany ? 'Select Funding Company' : 'Select Company';
  const { errors, touched, values, handleBlur, setFieldValue } = useFormikContext<FormValues>();
  const [showInfo, setShowInfo] = useState<boolean>(!!values?.company?.id);
  useEffect(() => {
    setShowInfo(!!values?.company?.id);
  }, [disabled, values?.company?.id]);
  const inputReference = useRef<HTMLInputElement>(null);
  const [companies, setCompanies] = useState<readonly InternalCompany[]>([]);
  const [companyInputValue, setCompanyInputValue] = useState<string>('');
  const hasSetDefault = useRef(false);
  const {
    loading: companiesLoading,
    data: companiesData,
    refetch: companiesRefetch
  } = useInternalCompaniesQuery({
    variables: {},
    fetchPolicy: 'network-only',
    skip: disabled && (!defaultCompany || !!values?.company?.id)
  });
  const companiesFetch = useMemo(
    () =>
      debounce(
        (request: { input: string }) =>
          companiesRefetch({ search: request.input }).then((rq) =>
            setCompanies((rq.data.internalCompanies.data as InternalCompany[]) || [])
          ),
        200
      ),
    [companiesRefetch]
  );
  useEffect(
    () => setCompanies((companiesData?.internalCompanies.data as InternalCompany[]) || []),
    [companiesData?.internalCompanies.data, companiesLoading]
  );
  const isMounted = useRef(false);
  useEffect(() => {
    if (disabled) return;
    if (isMounted.current) {
      companiesFetch({ input: companyInputValue });
    } else {
      isMounted.current = true;
    }
  }, [companyInputValue, companiesFetch, disabled]);
  useEffect(() => {
    if (defaultCompany && !values?.company?.id && companies.length > 0 && !hasSetDefault.current) {
      const defaultComp = companies.find((c) => c.isDefault);
      if (defaultComp) {
        setFieldValue('company', defaultComp);
        hasSetDefault.current = true;
      }
    }
  }, [defaultCompany, !values?.company?.id, companies, setFieldValue]);

  return (
    <>
      {values?.company?.id && showInfo && (
        <Stack direction={'row'}>
          <AssetListButtonOption
            logo={values?.company?.logo || null}
            name={values?.company?.legalName || ''}
            type={companyTypeLabel}
            showEditAction={!disabled}
            onClick={() => {
              if (disabled) return;
              setShowInfo(false);
              delay(() => inputReference.current?.focus(), 500);
            }}
          />
        </Stack>
      )}
      {!showInfo && (
        <ClickAwayListener
          onClickAway={() => {
            setShowInfo(disabled ? false : !!values?.company?.id);
          }}
        >
          <Autocomplete
            disabled={disabled}
            id={id}
            getOptionLabel={(option: InternalCompany) => option.legalName || ''}
            filterOptions={(options) => options}
            options={companies}
            autoComplete
            openOnFocus={false}
            value={(values.company?.id ? values.company as InternalCompany : null)}
            isOptionEqualToValue={(option: InternalCompany, value: InternalCompany) =>
              !!value?.id && option.id === value.id
            }
            onChange={(event: SyntheticEvent, newValue: InternalCompany | null) => {
              setFieldValue('company', newValue || null);
              setShowInfo(!!newValue?.id);
            }}
            onInputChange={(event: SyntheticEvent, newInputValue: string) => {
              setCompanyInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder={placeholder}
                inputRef={inputReference}
                name="company"
                onBlur={handleBlur}
                error={Boolean(touched.company && errors.company?.id)}
                helperText={touched.company && errors.company?.id}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: values?.company?.id && (
                    <AssetLogo logo={values?.company?.logo} name={values?.company?.legalName} size={'sm'} />
                  )
                }}
                sx={{
                  '& fieldset': { border: 'none' },
                  '& .MuiInputLabel-root': { padding: '14px 0 8px 16px' },
                  '& .MuiOutlinedInput-root': { padding: '14px 0 8px 16px' }
                }}
              />
            )}
            renderOption={(props, option) => {
               const { key, ...rest } = props;
               return <AssetListButtonOption logo={option.logo || null} key={key} name={option.legalName || ''} type={companyTypeLabel} {...rest} />;
            }}
          />
        </ClickAwayListener>
      )}
    </>
  );
};
